import {filter, Observable, Subject, takeUntil, tap} from 'rxjs';
import {AggregateEvent, AggregatePattern} from '../common/aggregate';
import {SubscriptionsMap} from '../common/subscriptions-map';
import {BroadcastEventSymbol} from '../common/symbols';
import {Constructor, EventContext, EventHandler, EventManagerMethods} from '../common/types';
import {BroadcastEventBus} from './broadcast-event-bus';

export class BroadcastEventManager implements EventManagerMethods {
	private readonly onDestroy$: Subject<void> = new Subject();
	private readonly subscriptions = new SubscriptionsMap();

	constructor() {
		BroadcastEventBus.eventsBus$
			.pipe(
				takeUntil(this.onDestroy$),
				tap((event) => this.handleEvent(event.event, event.args, event.context)),
			)
			.subscribe();
	}

	public subscribe<T>(event: Constructor<T>, handler: EventHandler<T>): void {
		this.subscriptions.add(event, handler);
	}

	public destroy() {
		this.onDestroy$.next();
		this.onDestroy$.complete();
		this.subscriptions.free();
	}

	public aggregate(pattern: AggregatePattern): AggregateEvent {
		return new AggregateEvent(pattern, this);
	}

	public fromEvent<T>(event: Constructor<T>): Observable<{args: T; context: EventContext}> {
		return BroadcastEventBus.eventsBus$
			.pipe(
				takeUntil(this.onDestroy$),
				filter(e => event.prototype[BroadcastEventSymbol]?.eventID === e.event),
			);
	}

	private handleEvent = (eventID: number, args: any, context: EventContext): void => {
		const event = this.getEvent(eventID);
		if(event) {
			this.subscriptions.get(event).forEach(s => s.next(args, context));
		}
	};

	private getEvent<T>(eventID: number): Constructor<T> {
		return this.subscriptions.events.find(e => e.prototype[BroadcastEventSymbol]?.eventID === eventID);
	}
}
